import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/api";
import { baseURL } from "@/api/utils/base-url";
import type { NetworxResponse } from "@/api/utils/types";
import { env } from "@/config";
import { MOCK_SERVICES } from "@/pages/homepage/mock/services";

export function useNetworxServices({ sortBy }: UseNetworxServicesOptions) {
  return useQuery<NetworxServicesData>({
    queryKey: ["networx-services", sortBy],
    queryFn: async () => {
      if (env.env === "LOCAL") {
        return getMockServicesData(sortBy);
      }

      const response = await axiosInstance.get<ServicesResponse>(
        "/services",
        {
          baseURL: baseURL.v2,
          meta: {
            disableToasts: true,
          },
        }
      );

      return getServicesData(response.data.response.return_data, sortBy);
    },
  });
}

function getMockServicesData(sortBy?: SortBy[] | SortBy): NetworxServicesData {
  return getServicesData(MOCK_SERVICES, sortBy);
}

function getServicesData(data: ServicesReturnData | undefined, sortBy?: SortBy[] | SortBy): NetworxServicesData {
  const mainServices = toServiceArray(data?.main)
    .map(mapService);
  const services = toServiceArray(data?.seo)
    .map(mapService);

  return {
    mainServices: sortServices(mainServices, sortBy),
    services: sortServices(services, sortBy),
  };
}

function mapService(service: SearchService, index: number): NetworxService {
  const {id, service_name, name, display_name, contractors} = service;

  return {
    id: String(id),
    pathname: `/${name}`,
    order: index + 1,
    service_name: service_name ?? "",
    display_name: formatServiceName(display_name ?? ""),
    contractors: contractors ?? "",
    icon: formatServiceIcon(display_name ?? ""),
  };
}

function toServiceArray(services?: ServicesCollection): SearchService[] {
  if (!services) {
    return [];
  }

  return Array.isArray(services) ? services : Object.values(services);
}

function sortServices(services: NetworxService[], sortBy?: SortBy[] | SortBy): NetworxService[] {
  if (!sortBy) {
    return services;
  }

  const sortCriteria = Array.isArray(sortBy) ? sortBy : [sortBy];

  return [...services].sort((a, b) => {
    for (const criteria of sortCriteria) {
      let comparison = 0;

      switch (criteria) {
        case "order":
          comparison = a.order - b.order;
          break;

        case "alphabetical":
          comparison = a.service_name.localeCompare(b.service_name ?? "");
          break;
      }

      if (comparison !== 0) {
        return comparison;
      }
    }

    return 0;
  });
}

function formatServiceName(name: string): string {
  return name.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatServiceIcon(name: string): string {
  return name.toLowerCase().replace(/ /g, "_");
}

export type NetworxService = {
  id: string;
  pathname: string;
  order: number;
  service_name: string;
  display_name: string;
  contractors: string;
  icon: string;
};

interface NetworxServicesData {
  mainServices: NetworxService[];
  services: NetworxService[];
}

interface SearchService {
  id: string | number;
  name: string;
  display_name?: string;
  service_name?: string;
  contractors?: string;
}

export interface ServicesReturnData {
  main?: ServicesCollection;
  seo?: ServicesCollection;
}

type ServicesCollection = SearchService[] | Record<string, SearchService>;
type ServicesResponse = NetworxResponse<unknown, ServicesReturnData>;

type SortBy = "alphabetical" | "order";
interface UseNetworxServicesOptions {
  /** if an array is provided, the sorts will be applied in the order of the array.
   * e.g. sortBy: ["alphabetical", "order"] will first sort alphabetically, then tie-break by order.
   */
  sortBy?: SortBy[] | SortBy;
}